import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, DollarSign, TrendingUp, Users, CreditCard } from "lucide-react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useRole } from "@/hooks/useRole";
import { AccessDenied } from "@/components/admin/games/AccessDenied";

interface PlatformCommission {
  mentor_id: string;
  mentor_name: string;
  mentor_email: string;
  commission_rate: number;
  total_payments: number;
  platform_earnings: number;
  mentor_earnings: number;
  session_count: number;
}

interface CommissionSummary {
  total_platform_earnings: number;
  total_mentor_earnings: number;
  total_sessions: number;
  total_mentors: number;
  average_commission_rate: number;
}

const FinancasAdmin = () => {
  const { role, loading: roleLoading } = useRole();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState<PlatformCommission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);

  useEffect(() => {
    if (role === 'admin') {
      fetchCommissionData();
    }
  }, [role]);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      console.log('🔍 [FinancasAdmin] Iniciando busca de dados de comissão...');

      // Buscar comissões da plataforma
      const { data: commissionsData, error: commissionsError } = await supabase
        .rpc('get_platform_commissions');

      if (commissionsError) {
        console.error('❌ [FinancasAdmin] Erro ao buscar comissões:', commissionsError);
        toast({
          title: 'Erro',
          description: `Erro ao buscar comissões: ${commissionsError.message}`,
          variant: 'destructive',
        });
        return;
      }

      // Buscar resumo das comissões
      const { data: summaryData, error: summaryError } = await supabase
        .rpc('get_platform_commission_summary');

      if (summaryError) {
        console.error('❌ [FinancasAdmin] Erro ao buscar resumo:', summaryError);
        toast({
          title: 'Erro',
          description: `Erro ao buscar resumo: ${summaryError.message}`,
          variant: 'destructive',
        });
        return;
      }

      console.log('✅ [FinancasAdmin] Dados obtidos:', {
        commissions: commissionsData || [],
        summary: summaryData?.[0] || null,
        commissionsLength: (commissionsData || []).length,
        summaryDetails: JSON.stringify(summaryData?.[0], null, 2)
      });

      // Verificar se há dados reais
      const commissionsEmpty = !commissionsData || commissionsData.length === 0;
      const summaryEmpty = !summaryData || !summaryData[0] || summaryData[0].total_sessions === 0;
      
      console.log('🔍 [FinancasAdmin] Verificação de dados:', {
        commissionsEmpty,
        summaryEmpty,
        hasRealData: !commissionsEmpty && !summaryEmpty
      });

      // Usar apenas dados reais - sem fallback para dados de exemplo
      setCommissions(commissionsData || []);
      setSummary(summaryData?.[0] || null);

      // Se não há dados, mostrar informação apropriada
      if (commissionsEmpty && summaryEmpty) {
        console.log('📊 [FinancasAdmin] Nenhum dado financeiro encontrado - estado limpo');
        toast({
          title: 'Sem dados financeiros',
          description: 'Nenhuma transação foi encontrada na plataforma ainda.',
          variant: 'default',
        });
      }

    } catch (error: any) {
      console.error('💥 [FinancasAdmin] Error completo:', error);
      toast({
        title: 'Erro',
        description: `Erro ao carregar dados financeiros: ${error.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f141c] pt-16">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (role !== "admin") {
    return <AccessDenied />;
  }

  if (loading) {
    return (
      <div className="bg-[#0f141c] min-h-screen pt-16 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0f141c] min-h-screen pt-16 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Relatório Financeiro</h1>
            <p className="text-gray-400 text-sm mt-1">
              Visualização de comissões e receitas da plataforma
            </p>
          </div>
          <div className="text-right">
            <Badge variant="outline" className="text-green-400 border-green-400 mb-2">
              {formatCurrency(summary?.total_platform_earnings || 0)} arrecadados
            </Badge>
            <p className="text-xs text-gray-400">
              Para gerenciar saques, acesse a página de Saques no menu lateral
            </p>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-green-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Receita Total</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(summary?.total_platform_earnings || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-blue-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Comissão Média</p>
                  <p className="text-2xl font-bold text-white">
                    {formatPercentage(summary?.average_commission_rate || 0.2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-purple-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Mentores Ativos</p>
                  <p className="text-2xl font-bold text-white">
                    {summary?.total_mentors || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <CreditCard className="h-8 w-8 text-orange-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Sessões Total</p>
                  <p className="text-2xl font-bold text-white">
                    {summary?.total_sessions || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Comissões por Mentor */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardHeader>
            <CardTitle className="text-white">Comissões por Mentor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2A3749]">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Mentor</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Taxa Comissão</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Sessões</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Total Vendas</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Nossa Comissão</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Ganho Mentor</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((commission) => (
                    <tr key={commission.mentor_id} className="border-b border-[#2A3749]/50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="text-white font-medium">{commission.mentor_name}</div>
                          <div className="text-gray-400 text-sm">{commission.mentor_email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          variant="outline" 
                          className={commission.commission_rate === 0.2 ? 
                            "text-green-400 border-green-400" : 
                            "text-orange-400 border-orange-400"
                          }
                        >
                          {formatPercentage(commission.commission_rate)}
                          {commission.commission_rate !== 0.2 && " (Personalizado)"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-white">{commission.session_count}</td>
                      <td className="py-3 px-4 text-white">
                        {formatCurrency(commission.total_payments)}
                      </td>
                      <td className="py-3 px-4 text-green-400 font-medium">
                        {formatCurrency(commission.platform_earnings)}
                      </td>
                      <td className="py-3 px-4 text-blue-400 font-medium">
                        {formatCurrency(commission.mentor_earnings)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {commissions.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  Nenhum dado de comissão encontrado
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinancasAdmin; 